import { BRAND } from "@/lib/brand-colors";

export const AXIS_TICK = { fill: "#86868b", fontSize: 11 } as const;
export const AXIS_TICK_BOLD = { fill: "#86868b", fontSize: 11, fontWeight: 600 } as const;
export const CATEGORY_TICK = { fill: "#1d1d1f", fontSize: 11, fontWeight: 600 } as const;
export const GRID_STROKE = "#e8e8ed";

export const BAR_ANIMATION = { animationDuration: 900, animationEasing: "ease-out" as const };
export const LINE_ANIMATION = { animationDuration: 1100, animationEasing: "ease-out" as const };

/**
 * Reusable SVG gradient defs. Render once inside each chart, then reference
 * fills via `url(#...)`.
 */
export function ChartGradientDefs() {
  return (
    <defs>
      <linearGradient id="gradAccentY" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={BRAND.accent} stopOpacity={1} />
        <stop offset="100%" stopColor={BRAND.accent} stopOpacity={0.55} />
      </linearGradient>
      <linearGradient id="gradAccentX" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={BRAND.accent} stopOpacity={0.6} />
        <stop offset="100%" stopColor={BRAND.accent} stopOpacity={1} />
      </linearGradient>
      <linearGradient id="gradForestY" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={BRAND.foreground} stopOpacity={0.95} />
        <stop offset="100%" stopColor={BRAND.foreground} stopOpacity={0.6} />
      </linearGradient>
      <linearGradient id="gradForestX" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={BRAND.foreground} stopOpacity={0.65} />
        <stop offset="100%" stopColor={BRAND.foreground} stopOpacity={0.95} />
      </linearGradient>
      <linearGradient id="gradAccentArea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={BRAND.accent} stopOpacity={0.35} />
        <stop offset="100%" stopColor={BRAND.accent} stopOpacity={0.02} />
      </linearGradient>
      <linearGradient id="gradGreenArea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#34c759" stopOpacity={0.3} />
        <stop offset="100%" stopColor="#34c759" stopOpacity={0.02} />
      </linearGradient>
      <linearGradient id="gradOrangeArea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ff9500" stopOpacity={0.28} />
        <stop offset="100%" stopColor="#ff9500" stopOpacity={0.02} />
      </linearGradient>
    </defs>
  );
}

export function ChartTooltip({
  active,
  payload,
  label,
  valueLabel,
  formatValue,
  accentColor = BRAND.accent,
}: {
  active?: boolean;
  payload?: Array<{ value?: unknown }>;
  label?: string | number;
  valueLabel: string;
  formatValue?: (value: number) => string;
  accentColor?: string;
}) {
  const raw = payload?.[0]?.value;
  if (!active || raw == null) return null;
  const value = Number(raw);
  const display = formatValue && Number.isFinite(value) ? formatValue(value) : String(raw);

  return (
    <div className="animate-scale rounded-2xl border border-white/60 bg-surface/95 px-3.5 py-2.5 shadow-[var(--shadow-pop)] backdrop-blur-md">
      {label != null && label !== "" ? (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</p>
      ) : null}
      <p className="mt-0.5 flex items-center gap-1.5 text-sm font-extrabold tracking-tight text-foreground">
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: accentColor }}
        />
        {display}
        {valueLabel ? <span className="font-semibold text-muted">{valueLabel}</span> : null}
      </p>
    </div>
  );
}
